import { useId } from "react";
import { useTranslation } from "react-i18next";

import Label from "./Label";
import Input from "./input/InputField";
import WhatsAppIcon from "../icons/WhatsAppIcon";

interface WhatsAppNumberFieldProps {
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    /** i18n key of the problem with the number, if any. */
    error?: string;
    /** The patient switched WhatsApp off in their portal: said, never editable here. */
    refusedByPatient?: boolean;
}

/**
 * The patient's WhatsApp number, next to their phone and apart from it: the
 * two can differ, and every WhatsApp message goes to this one. Optional.
 *
 * A national number is accepted as typed and completed with the patient's
 * country code when the form is saved.
 */
export default function WhatsAppNumberField({
    value,
    onChange,
    onBlur,
    error,
    refusedByPatient = false,
}: WhatsAppNumberFieldProps) {
    const { t } = useTranslation();
    const id = useId();
    const hintId = `${id}-hint`;
    const errorId = `${id}-error`;

    return (
        <div>
            <Label htmlFor={id}>
                <span className="inline-flex items-center gap-1.5">
                    <WhatsAppIcon className="size-4 text-green-600 dark:text-green-400" />
                    {t("patient.whatsapp")}
                </span>
            </Label>
            <Input
                id={id}
                type="tel"
                placeholder="+34 600 000 000"
                startIcon={<WhatsAppIcon className="size-5" />}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                error={Boolean(error)}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? errorId : hintId}
            />
            {error ? (
                <p id={errorId} className="mt-1 text-xs text-red-500">
                    {t(error)}
                </p>
            ) : (
                <p id={hintId} className="mt-1 text-xs text-content-tertiary">
                    {t("patient.whatsappHint")}
                </p>
            )}
            {refusedByPatient && (
                <p className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-400">
                    {t("patient.whatsappOffHint")}
                </p>
            )}
        </div>
    );
}
